import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UseLogsReturn } from "../types";

/**
 * URL parameter names
 */
const URL_PARAMS = {
  QUERY: "query",
  FROM: "from",
  TO: "to",
  TIMEZONE: "tz",
  EXECUTE: "execute",
  LIVE: "live",
} as const;

export interface UseLogsUrlStateOptions {
  /** Reference to useLogs return value */
  logs: UseLogsReturn;
  /** Current timezone value */
  timezone?: string;
  /** Callback to set timezone */
  onTimezoneChange?: (tz: string) => void;
  /** Whether the component has mounted (from useLogs) */
  mounted?: boolean;
  /** Prevent URL sync while input is focused */
  isInputFocused?: boolean;
}

export interface UseLogsUrlStateReturn {
  /** Whether execute/live was requested via URL */
  shouldExecute: boolean;
  /** Whether live mode was requested via URL */
  shouldStartLive: boolean;
  /** Mark URL triggers as handled */
  markUrlTriggersHandled: () => void;
  /** Whether URL has been read and initial state set */
  isUrlInitialized: boolean;
}

/**
 * Hook to sync logs state with URL search params
 *
 * Usage:
 * ```tsx
 * const logs = useLogs({ ... });
 * const { shouldExecute, shouldStartLive, markUrlTriggersHandled } = useLogsUrlState({
 *   logs,
 *   timezone: timezoneValue,
 *   onTimezoneChange: (tz) => dispatch(setTimezoneValue(tz)),
 *   mounted: true,
 * });
 *
 * useEffect(() => {
 *   if (shouldExecute && !shouldStartLive) {
 *     logs.executeQuery();
 *   } else if (shouldStartLive) {
 *     logs.startLive();
 *   }
 *   markUrlTriggersHandled();
 * }, [shouldExecute, shouldStartLive]);
 * ```
 */
export function useLogsUrlState({
  logs,
  timezone,
  onTimezoneChange,
  mounted = true,
  isInputFocused = false,
}: UseLogsUrlStateOptions): UseLogsUrlStateReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const initializedRef = useRef(false);
  const urlReadCompleteRef = useRef(false);
  const urlTriggersHandledRef = useRef(false);
  const [isUrlInitialized, setIsUrlInitialized] = useState(false);

  // Read initial values from URL on mount (ONCE)
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const urlQuery = searchParams.get(URL_PARAMS.QUERY);
    const urlFrom = searchParams.get(URL_PARAMS.FROM);
    const urlTo = searchParams.get(URL_PARAMS.TO);
    const urlTz = searchParams.get(URL_PARAMS.TIMEZONE);

    if (urlQuery) {
      logs.setQuery(urlQuery);
    }
    if (urlFrom) {
      logs.setFrom(urlFrom);
    }
    if (urlTo) {
      logs.setTo(urlTo);
    }
    if (urlTz && onTimezoneChange) {
      onTimezoneChange(urlTz);
    }

    // Mark URL as initialized after a tick to allow React state to settle
    // This prevents Filter → Query sync from overwriting URL values
    setTimeout(() => {
      urlReadCompleteRef.current = true;
      setIsUrlInitialized(true);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - run once on mount only

  // Sync state to URL - only after read is complete, not while focused
  useEffect(() => {
    if (!mounted || isInputFocused || !urlReadCompleteRef.current) return;

    const newParams = new URLSearchParams();

    if (logs.query) {
      newParams.set(URL_PARAMS.QUERY, logs.query);
    }
    if (logs.from) {
      newParams.set(URL_PARAMS.FROM, logs.from);
    }
    if (logs.to) {
      newParams.set(URL_PARAMS.TO, logs.to);
    }
    // Write timezone to URL - include UTC if explicitly set (e.g., from job executions link)
    // Skip only when timezone matches user's default (not from URL override)
    if (timezone) {
      newParams.set(URL_PARAMS.TIMEZONE, timezone);
    }

    // Only update if params actually changed
    const newParamsStr = newParams.toString();
    const currentParamsStr = searchParams.toString();
    if (currentParamsStr !== newParamsStr) {
      setSearchParams(newParams, { replace: true });
    }
    // Note: searchParams removed from deps to prevent feedback loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, isInputFocused, logs.query, logs.from, logs.to, timezone, setSearchParams]);

  // Check if URL requests immediate execution - wait for URL to be fully initialized first
  const shouldExecute = searchParams.has(URL_PARAMS.EXECUTE) && !urlTriggersHandledRef.current && isUrlInitialized;
  const shouldStartLive = searchParams.get(URL_PARAMS.LIVE) === "true" && !urlTriggersHandledRef.current && isUrlInitialized;

  const markUrlTriggersHandled = useCallback(() => {
    urlTriggersHandledRef.current = true;
    // Remove execute and live params from URL after handling
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(URL_PARAMS.EXECUTE);
    newParams.delete(URL_PARAMS.LIVE);
    if (newParams.toString() !== searchParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return {
    shouldExecute,
    shouldStartLive,
    markUrlTriggersHandled,
    isUrlInitialized,
  };
}
